import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import { Play, Square, RotateCcw, Keyboard } from 'lucide-react';
import {
  createSimulation, makeInput, ARENA, SIM_FPS_VALUES,
} from '../../simulator';
import type { Program, Simulation, SimError, SimFps, KeyCode, SimInput } from '../../simulator';

/**
 * Arène néon du simulateur `Time.deltaTime`.
 *
 * - `<canvas>` 16:9, DPR-aware, fond #05060f, grille cyan 10 %, bords magenta, vaisseau cyan + halo.
 * - Boucle `requestAnimationFrame` + accumulateur : `sim.step()` exactement `fps` fois par seconde
 *   réelle → à 5 FPS l'objet saute par à-coups (c'est le but).
 * - Clavier capturé UNIQUEMENT quand l'arène a le focus (tabIndex + clic) : flèches, ZQSD, WASD,
 *   espace ; `preventDefault` sur ces touches (sinon la page défile) ; `keysDown` vidé après chaque
 *   snapshot d'entrée (README G1 §keysDown).
 * - HUD : cadence, image n°, position, distance parcourue par seconde (mesurée sur la dernière
 *   seconde simulée), temps simulé, champ `speed`.
 */

export type ArenaStatus = 'idle' | 'running' | 'stopped' | 'error';

export interface NeonArenaHandle {
  /** Compile déjà faite par le parent : démarre (ou redémarre) la simulation de ce programme. */
  play: (program: Program) => void;
  stop: () => void;
  reset: () => void;
  focus: () => void;
}

interface NeonArenaProps {
  fps: SimFps;
  onFpsChange: (fps: SimFps) => void;
  /** Le parent compile puis appelle `play()` ; ce bouton ne fait que demander la compilation. */
  onRequestPlay: () => void;
  onRuntimeError: (err: SimError) => void;
  onStatusChange?: (status: ArenaStatus) => void;
  /** Rien à jouer tant que le parent n'a pas fourni de programme (bouton Play toujours actif). */
  hasProgram: boolean;
}

interface Hud {
  frame: number;
  x: number;
  y: number;
  perSecond: number;
  elapsed: number;
  speed: number | null;
  outside: boolean;
}

interface Sample { t: number; x: number; y: number }

const KEY_MAP: Record<string, KeyCode> = {
  ArrowLeft: 'LeftArrow',
  ArrowRight: 'RightArrow',
  ArrowUp: 'UpArrow',
  ArrowDown: 'DownArrow',
  w: 'W', W: 'W',
  a: 'A', A: 'A',
  s: 'S', S: 'S',
  d: 'D', D: 'D',
  z: 'Z', Z: 'Z',
  q: 'Q', Q: 'Q',
  ' ': 'Space',
};

const TRAIL_LENGTH = 24;
const HUD_INTERVAL_MS = 120;
const MAX_FRAME_DELTA = 0.25; // s — évite la spirale de la mort après un onglet en arrière-plan
const EMPTY_HUD: Hud = { frame: 0, x: 0, y: 0, perSecond: 0, elapsed: 0, speed: null, outside: false };

const CYAN = '#19f5ff';
const MAGENTA = '#ff2bd6';
const BG = '#05060f';

export const NeonArena = forwardRef<NeonArenaHandle, NeonArenaProps>(
  ({ fps, onFpsChange, onRequestPlay, onRuntimeError, onStatusChange, hasProgram }, ref) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const simRef = useRef<Simulation | null>(null);
    const programRef = useRef<Program | null>(null);
    const rafRef = useRef<number | null>(null);
    const lastTsRef = useRef<number | null>(null);
    const accRef = useRef(0);
    const samplesRef = useRef<Sample[]>([]);
    const trailRef = useRef<Sample[]>([]);
    const headingRef = useRef<{ x: number; y: number }>({ x: 0, y: 1 });
    const lastHudTsRef = useRef(0);
    const statusRef = useRef<ArenaStatus>('idle');
    const fpsRef = useRef<SimFps>(fps);
    fpsRef.current = fps;

    const held = useRef(new Set<KeyCode>());
    const pressed = useRef(new Set<KeyCode>());
    const released = useRef(new Set<KeyCode>());

    const [status, setStatus] = useState<ArenaStatus>('idle');
    const [focused, setFocused] = useState(false);
    const [hud, setHud] = useState<Hud>(EMPTY_HUD);
    const [logs, setLogs] = useState<string[]>([]);

    const changeStatus = useCallback((next: ArenaStatus) => {
      statusRef.current = next;
      setStatus(next);
      onStatusChange?.(next);
    }, [onStatusChange]);

    // --- Entrées clavier → SimInput (snapshot par frame simulée) ---
    const inputProvider = useCallback((): SimInput => {
      const h = held.current;
      const horizontal = ((h.has('RightArrow') || h.has('D') ? 1 : 0) - (h.has('LeftArrow') || h.has('A') || h.has('Q') ? 1 : 0)) as -1 | 0 | 1;
      const vertical = ((h.has('UpArrow') || h.has('W') || h.has('Z') ? 1 : 0) - (h.has('DownArrow') || h.has('S') ? 1 : 0)) as -1 | 0 | 1;
      const snapshot = makeInput({
        horizontal,
        vertical,
        keys: new Set(h),
        keysDown: new Set(pressed.current),
        keysUp: new Set(released.current),
      });
      pressed.current.clear();
      released.current.clear();
      return snapshot;
    }, []);

    // --- Dessin ---
    const draw = useCallback(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      const cssW = canvas.clientWidth;
      const cssH = canvas.clientHeight;
      if (cssW === 0 || cssH === 0) return;
      const targetW = Math.round(cssW * dpr);
      const targetH = Math.round(cssH * dpr);
      if (canvas.width !== targetW || canvas.height !== targetH) {
        canvas.width = targetW;
        canvas.height = targetH;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const scale = Math.min(cssW / (ARENA.halfWidth * 2 * 1.08), cssH / (ARENA.halfHeight * 2 * 1.08));
      const cx = cssW / 2;
      const cy = cssH / 2;
      const toX = (x: number) => cx + x * scale;
      const toY = (y: number) => cy - y * scale;

      ctx.fillStyle = BG;
      ctx.fillRect(0, 0, cssW, cssH);

      // Grille discrète (1 unité)
      ctx.lineWidth = 1;
      for (let gx = -ARENA.halfWidth; gx <= ARENA.halfWidth; gx++) {
        ctx.strokeStyle = gx === 0 ? 'rgba(25, 245, 255, 0.22)' : 'rgba(25, 245, 255, 0.10)';
        ctx.beginPath();
        ctx.moveTo(toX(gx), toY(-ARENA.halfHeight));
        ctx.lineTo(toX(gx), toY(ARENA.halfHeight));
        ctx.stroke();
      }
      for (let gy = -ARENA.halfHeight; gy <= ARENA.halfHeight; gy++) {
        ctx.strokeStyle = gy === 0 ? 'rgba(25, 245, 255, 0.22)' : 'rgba(25, 245, 255, 0.10)';
        ctx.beginPath();
        ctx.moveTo(toX(-ARENA.halfWidth), toY(gy));
        ctx.lineTo(toX(ARENA.halfWidth), toY(gy));
        ctx.stroke();
      }

      // Bords de l'arène (magenta néon)
      ctx.save();
      ctx.strokeStyle = MAGENTA;
      ctx.shadowColor = MAGENTA;
      ctx.shadowBlur = 14;
      ctx.lineWidth = 2;
      ctx.strokeRect(toX(-ARENA.halfWidth), toY(ARENA.halfHeight), ARENA.halfWidth * 2 * scale, ARENA.halfHeight * 2 * scale);
      ctx.restore();

      const sim = simRef.current;
      const pos = sim ? sim.state.position : { x: 0, y: 0 };

      // Traînée courte
      const trail = trailRef.current;
      for (let i = 0; i < trail.length; i++) {
        const alpha = ((i + 1) / trail.length) * 0.45;
        ctx.fillStyle = `rgba(25, 245, 255, ${alpha.toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(toX(trail[i].x), toY(trail[i].y), 2.5 + (i / trail.length) * 2, 0, Math.PI * 2);
        ctx.fill();
      }

      // Vaisseau (triangle orienté) + halo
      const sx = toX(pos.x);
      const sy = toY(pos.y);
      const heading = headingRef.current;
      const angle = Math.atan2(-heading.y, heading.x); // repère écran (y vers le bas)
      const r = 13;
      ctx.save();
      ctx.translate(sx, sy);
      ctx.rotate(angle);
      ctx.shadowColor = CYAN;
      ctx.shadowBlur = 26;
      ctx.fillStyle = CYAN;
      ctx.beginPath();
      ctx.moveTo(r, 0);
      ctx.lineTo(-r * 0.7, r * 0.65);
      ctx.lineTo(-r * 0.35, 0);
      ctx.lineTo(-r * 0.7, -r * 0.65);
      ctx.closePath();
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.beginPath();
      ctx.arc(-r * 0.05, 0, 2.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Indication hors arène
      if (Math.abs(pos.x) > ARENA.halfWidth || Math.abs(pos.y) > ARENA.halfHeight) {
        ctx.fillStyle = MAGENTA;
        ctx.font = '600 12px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('hors de l\'arène — Mathf.Clamp ?', cx, 18);
      }

      if (!sim) {
        ctx.fillStyle = 'rgba(25, 245, 255, 0.55)';
        ctx.font = '600 13px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('▶ Compiler & Jouer pour lancer la simulation', cx, cssH - 14);
      }
    }, []);

    // --- HUD ---
    const computeHud = useCallback((): Hud => {
      const sim = simRef.current;
      if (!sim) return EMPTY_HUD;
      const samples = samplesRef.current;
      const now = sim.state.elapsed;
      let dist = 0;
      for (let i = 1; i < samples.length; i++) {
        if (samples[i].t > now - 1 + 1e-9) {
          const dx = samples[i].x - samples[i - 1].x;
          const dy = samples[i].y - samples[i - 1].y;
          dist += Math.sqrt(dx * dx + dy * dy);
        }
      }
      const window = Math.min(1, now);
      const fields = sim.getFields();
      const speedField = typeof fields.speed === 'number' ? fields.speed : null;
      const pos = sim.state.position;
      return {
        frame: sim.state.frame,
        x: pos.x,
        y: pos.y,
        perSecond: window > 0 ? dist / window : 0,
        elapsed: now,
        speed: speedField,
        outside: Math.abs(pos.x) > ARENA.halfWidth || Math.abs(pos.y) > ARENA.halfHeight,
      };
    }, []);

    const recordSample = useCallback((sim: Simulation) => {
      const pos = sim.state.position;
      const t = sim.state.elapsed;
      const samples = samplesRef.current;
      const last = samples[samples.length - 1];
      if (last) {
        const dx = pos.x - last.x;
        const dy = pos.y - last.y;
        if (dx !== 0 || dy !== 0) headingRef.current = { x: dx, y: dy };
      }
      samples.push({ t, x: pos.x, y: pos.y });
      while (samples.length > 2 && samples[0].t < t - 1.5) samples.shift();
      const trail = trailRef.current;
      trail.push({ t, x: pos.x, y: pos.y });
      while (trail.length > TRAIL_LENGTH) trail.shift();
    }, []);

    const stopLoop = useCallback(() => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = null;
      accRef.current = 0;
    }, []);

    const tick = useCallback((ts: number) => {
      const sim = simRef.current;
      if (!sim || statusRef.current !== 'running') return;
      if (lastTsRef.current === null) lastTsRef.current = ts;
      let elapsed = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;
      if (elapsed > MAX_FRAME_DELTA) elapsed = MAX_FRAME_DELTA;
      accRef.current += elapsed;
      const stepDt = 1 / sim.fps;
      let failed: SimError | null = null;
      while (accRef.current >= stepDt) {
        accRef.current -= stepDt;
        const err = sim.step();
        recordSample(sim);
        if (err) { failed = err; break; }
      }
      draw();
      if (ts - lastHudTsRef.current > HUD_INTERVAL_MS || failed) {
        lastHudTsRef.current = ts;
        setHud(computeHud());
        setLogs(sim.state.logs.slice(-6));
      }
      if (failed) {
        stopLoop();
        changeStatus('error');
        onRuntimeError(failed);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    }, [changeStatus, computeHud, draw, onRuntimeError, recordSample, stopLoop]);

    const startSimulation = useCallback((program: Program) => {
      stopLoop();
      programRef.current = program;
      samplesRef.current = [];
      trailRef.current = [];
      headingRef.current = { x: 0, y: 1 };
      pressed.current.clear();
      released.current.clear();
      const sim = createSimulation(program, { fps: fpsRef.current, input: inputProvider });
      simRef.current = sim;
      const err = sim.start();
      samplesRef.current.push({ t: 0, x: sim.state.position.x, y: sim.state.position.y });
      setHud(computeHud());
      setLogs(sim.state.logs.slice(-6));
      draw();
      if (err) {
        changeStatus('error');
        onRuntimeError(err);
        return;
      }
      changeStatus('running');
      rafRef.current = requestAnimationFrame(tick);
    }, [changeStatus, computeHud, draw, inputProvider, onRuntimeError, stopLoop, tick]);

    const stop = useCallback(() => {
      stopLoop();
      if (simRef.current) setHud(computeHud());
      changeStatus(simRef.current ? 'stopped' : 'idle');
    }, [changeStatus, computeHud, stopLoop]);

    const reset = useCallback(() => {
      stopLoop();
      simRef.current = null;
      programRef.current = null;
      samplesRef.current = [];
      trailRef.current = [];
      headingRef.current = { x: 0, y: 1 };
      held.current.clear();
      pressed.current.clear();
      released.current.clear();
      setHud(EMPTY_HUD);
      setLogs([]);
      changeStatus('idle');
      draw();
    }, [changeStatus, draw, stopLoop]);

    useImperativeHandle(ref, () => ({
      play: (program: Program) => {
        startSimulation(program);
        wrapperRef.current?.focus();
      },
      stop,
      reset,
      focus: () => wrapperRef.current?.focus(),
    }), [reset, startSimulation, stop]);

    // Changement de cadence pendant le jeu : on redémarre la simulation à la nouvelle cadence
    // (le moteur fige `fps` à la création) — le vaisseau repart du centre.
    const handleFpsChange = (next: SimFps) => {
      if (next === fps) return;
      fpsRef.current = next;
      onFpsChange(next);
      if (statusRef.current === 'running' && programRef.current) {
        startSimulation(programRef.current);
        wrapperRef.current?.focus();
      }
    };

    // Redessin au redimensionnement (DPR / largeur de colonne)
    useEffect(() => {
      draw();
      const canvas = canvasRef.current;
      if (!canvas || typeof ResizeObserver === 'undefined') return;
      const observer = new ResizeObserver(() => draw());
      observer.observe(canvas);
      return () => observer.disconnect();
    }, [draw]);

    useEffect(() => () => stopLoop(), [stopLoop]);

    // --- Clavier (uniquement quand l'arène a le focus) ---
    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      const key = KEY_MAP[e.key];
      if (!key) return;
      e.preventDefault();
      if (!e.repeat && !held.current.has(key)) pressed.current.add(key);
      held.current.add(key);
    };
    const onKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
      const key = KEY_MAP[e.key];
      if (!key) return;
      e.preventDefault();
      held.current.delete(key);
      released.current.add(key);
    };
    const onBlur = () => {
      setFocused(false);
      held.current.clear();
    };

    const fmt = (v: number) => v.toFixed(2);

    return (
      <div className="space-y-3">
        {/* Arène : focusable, capture le clavier seulement ici */}
        <div
          ref={wrapperRef}
          tabIndex={0}
          role="application"
          aria-label="Arène du simulateur — cliquez puis utilisez les flèches"
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onFocus={() => setFocused(true)}
          onBlur={onBlur}
          onMouseDown={() => wrapperRef.current?.focus()}
          data-testid="neon-arena"
          className={`relative w-full aspect-video rounded-xl overflow-hidden outline-none border-2 transition-colors ${focused ? 'border-cyan-400 shadow-[0_0_18px_rgba(25,245,255,0.35)]' : 'border-gray-700 dark:border-gray-600'}`}
          style={{ backgroundColor: BG }}
        >
          <canvas ref={canvasRef} className="block w-full h-full" aria-hidden="true" />
          {status === 'running' && !focused && (
            <div className="absolute inset-x-0 bottom-3 flex justify-center pointer-events-none">
              <div className="flex items-center gap-2 bg-black/70 text-cyan-200 text-xs font-medium px-3 py-1.5 rounded-full border border-cyan-500/50">
                <Keyboard className="w-3.5 h-3.5" />
                Cliquez dans l'arène puis utilisez les flèches (ou ZQSD / WASD)
              </div>
            </div>
          )}
          {status === 'error' && (
            <div className="absolute inset-x-0 top-3 flex justify-center pointer-events-none">
              <div className="bg-red-900/80 text-red-200 text-xs font-medium px-3 py-1.5 rounded-full border border-red-500/60">
                Simulation arrêtée : erreur à l'exécution (voir la console)
              </div>
            </div>
          )}
        </div>

        {/* Contrôles */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md overflow-hidden border border-gray-300 dark:border-gray-600" role="group" aria-label="Cadence d'images simulée">
            {SIM_FPS_VALUES.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => handleFpsChange(value)}
                aria-pressed={fps === value}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${fps === value
                  ? 'bg-cyan-500 text-gray-900'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-cyan-50 dark:hover:bg-gray-700'}`}
              >
                {value} FPS
              </button>
            ))}
          </div>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onRequestPlay}
            className="inline-flex items-center px-3 py-1.5 bg-cyan-500 hover:bg-cyan-400 text-gray-900 text-sm font-semibold rounded-md transition-colors"
          >
            <Play className="w-4 h-4 mr-1" />
            Compiler &amp; Jouer
          </button>
          <button
            type="button"
            onClick={stop}
            disabled={status !== 'running'}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Square className="w-4 h-4 mr-1" />
            Stop
          </button>
          <button
            type="button"
            onClick={reset}
            disabled={status === 'idle' && !hasProgram}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset
          </button>
        </div>

        {/* HUD */}
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs" data-testid="sim-hud">
          <HudCell label="Cadence" value={`${fps} FPS`} />
          <HudCell label="Image n°" value={String(hud.frame)} />
          <HudCell label="Position x / y" value={`${fmt(hud.x)} / ${fmt(hud.y)}`} accent={hud.outside ? 'magenta' : undefined} />
          <HudCell label="Distance / s (dernière seconde)" value={`${fmt(hud.perSecond)} u/s`} accent="cyan" testId="hud-per-second" />
          <HudCell label="Temps simulé" value={`${fmt(hud.elapsed)} s`} />
          <HudCell label="speed (champ)" value={hud.speed === null ? '—' : `${hud.speed} u/s`} />
        </dl>

        {logs.length > 0 && (
          <div className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 font-mono text-xs text-gray-300 space-y-0.5">
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Debug.Log</div>
            {logs.map((line, i) => <div key={i} className="whitespace-pre-wrap break-words">{line}</div>)}
          </div>
        )}
      </div>
    );
  }
);

NeonArena.displayName = 'NeonArena';

const HudCell: React.FC<{ label: string; value: string; accent?: 'cyan' | 'magenta'; testId?: string }> = ({ label, value, accent, testId }) => (
  <div className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2">
    <dt className="text-[10px] uppercase tracking-wide text-gray-500">{label}</dt>
    <dd
      data-testid={testId}
      className={`font-mono text-sm ${accent === 'cyan' ? 'text-cyan-300' : accent === 'magenta' ? 'text-pink-400' : 'text-gray-100'}`}
    >
      {value}
    </dd>
  </div>
);

export default NeonArena;
